// ============================================
// components/Settings.js
// ============================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Switch,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  AccountCircle,
  Notifications,
  Lock,
  Info,
  Email,
  Person,
  ExitToApp,
  Delete,
  ChevronRight,
  Verified,
  Warning,
  Password
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { authService } from '../services/authService';
import { auth, db, functions } from '../firebase';
import { sendEmailVerification, updatePassword, signOut } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { resetFcmToken } from '../models/notificationModel';
import { updateUserDeviceToken, removeUserDeviceToken } from '../models/userModel';

import NotificationAlert from './common/NotificationAlert';
import { ROUTES, MESSAGES, ERRORS } from '../utils/constants';

const Settings = () => {
  const navigate = useNavigate();
  const { user, userData, emailVerified } = useAuth();
  const { permission: notificationPermission, requestPermission } = useNotifications(user?.uid);
  
  // State management
  const [notification, setNotification] = useState({ open: false, message: '', title: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState('');
  const [formData, setFormData] = useState({
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Dialog handlers
  const handleOpenDialog = (dialogName) => {
    setOpenDialog(dialogName);
    // Reset form data when opening dialogs
    if (dialogName === 'editProfile') {
      setFormData({
        ...formData,
        firstName: userData?.firstName || '',
        lastName: userData?.lastName || ''
      });
    } else if (dialogName === 'changePassword') {
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  };

  // Reset push subscription: delete current token and fetch a fresh one
  const handleResetPush = async () => {
    try {
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) throw new Error('VAPID key is not defined');
      setLoading(true);

      // Best-effort: get current token (without prompting) by reusing our model helper
      let oldToken = null;
      try {
        const mod = await import('../models/notificationModel');
        oldToken = await mod.requestNotificationPermission(vapidKey).catch(() => null);
      } catch (_) {}

      const newToken = await resetFcmToken(vapidKey);
      if (user?.uid) {
        if (oldToken && oldToken !== newToken) {
          await removeUserDeviceToken(user.uid, oldToken);
        }
        await updateUserDeviceToken(user.uid, newToken);
      }
      setNotification({ open: true, message: 'Push subscription reset', title: 'Success', severity: 'success' });
    } catch (e) {
      setNotification({ open: true, message: e.message || 'Failed to reset push', title: 'Error', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseDialog = () => {
    setOpenDialog('');
    setFormData({
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };
  
  // Email verification
  const handleResendVerification = async () => {
    if (!user || emailVerified) return;
    
    setLoading(true);
    try {
      await sendEmailVerification(user);
      setNotification({
        open: true,
        message: MESSAGES.EMAIL_VERIFICATION_SENT,
        title: 'Success',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to send verification email',
        title: 'Error',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Profile update
  const handleUpdateProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      });
      
      setNotification({
        open: true,
        message: 'Profile updated successfully',
        title: 'Success',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to update profile',
        title: 'Error',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Password change
  const handleChangePassword = async () => {
    if (!user) return;
    
    // Validation
    if (formData.newPassword !== formData.confirmPassword) {
      setNotification({
        open: true,
        message: 'Passwords do not match',
        title: 'Error',
        severity: 'error'
      });
      return;
    }
    
    if (formData.newPassword.length < 6) {
      setNotification({
        open: true,
        message: ERRORS.PASSWORD_TOO_SHORT,
        title: 'Error',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    try {
      // Re-authenticate first
      const result = await authService.login(user.email, formData.currentPassword);
      if (!result.success) {
        throw new Error('Current password is incorrect');
      }
      
      // Update password
      await updatePassword(user, formData.newPassword);
      
      setNotification({
        open: true,
        message: 'Password changed successfully',
        title: 'Success',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to change password',
        title: 'Error',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Account deletion (server-side via callable function)
  const handleDeleteAccount = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'deleteAccount');
      await fn();
      // Clear local auth state
      await signOut(auth);
      setNotification({
        open: true,
        message: 'Your account has been deleted.',
        title: 'Account Deleted',
        severity: 'success'
      });
      handleCloseDialog();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      setNotification({
        open: true,
        message: error?.message || 'Failed to delete account. Please try again.',
        title: 'Error',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Notification toggle
  const handleNotificationToggle = async () => {
    if (notificationPermission === 'default') {
      const result = await requestPermission();
      if (!result.success) {
        setNotification({
          open: true,
          message: result.error || 'Failed to enable notifications',
          title: 'Error',
          severity: 'error'
        });
      }
    } else if (notificationPermission === 'granted') {
      setNotification({
        open: true,
        message: 'To disable notifications, please use your browser settings',
        title: 'Info',
        severity: 'info'
      });
    }
  };
  
  // Logout
  const handleLogout = async () => {
    setLoading(true);
    const result = await authService.logout();
    if (result.success) {
      navigate(ROUTES.LOGIN);
    } else {
      setNotification({
        open: true,
        message: result.error || 'Failed to logout',
        title: 'Error',
        severity: 'error'
      });
      setLoading(false);
    }
  };
  
  if (!user) {
    return null;
  }
  
  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Button 
          onClick={() => navigate(ROUTES.HOME)}
          sx={{ mb: 2 }}
        >
          ← Back to Home
        </Button>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Settings
        </Typography>
      </Box>
      
      {/* Account Section */}
      <Paper elevation={0} variant="glass" sx={{ mb: 2 }}>
        <List>
          <ListItem>
            <ListItemIcon>
              <AccountCircle />
            </ListItemIcon>
            <ListItemText 
              primary="Account" 
              secondary={user?.email}
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
          <Divider variant="inset" component="li" />
          
          {/* Email Verification Status */}
          <ListItem>
            <ListItemIcon>
              {emailVerified ? <Verified color="success" /> : <Warning color="warning" />}
            </ListItemIcon>
            <ListItemText 
              primary="Email Verification"
              secondary={emailVerified ? "Verified" : "Not verified"}
            />
            {!emailVerified && (
              <ListItemSecondaryAction>
                <Button
                  size="small"
                  onClick={handleResendVerification}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Resend'}
                </Button>
              </ListItemSecondaryAction>
            )}
          </ListItem>
          
          {/* Edit Profile */}
          <ListItem button onClick={() => handleOpenDialog('editProfile')}>
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText 
              primary="Profile"
              secondary={`${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || 'Not set'}
            />
            <ChevronRight />
          </ListItem>
          
          {/* Change Password */}
          <ListItem button onClick={() => handleOpenDialog('changePassword')}>
            <ListItemIcon>
              <Password />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
            <ChevronRight />
          </ListItem>
        </List>
      </Paper>
      
      {/* Notifications Section */}
      <Paper elevation={0} variant="glass" sx={{ mb: 2 }}>
        <List>
          <ListItem>
            <ListItemIcon>
              <Notifications />
            </ListItemIcon>
            <ListItemText 
              primary="Notifications"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
          <Divider variant="inset" component="li" />
          
          <ListItem>
            <ListItemText 
              primary="Push Notifications"
              secondary={
                notificationPermission === 'granted' 
                  ? "Enabled - You'll receive porchlight and friend notifications"
                  : notificationPermission === 'denied'
                  ? "Blocked - Enable in browser settings"
                  : "Disabled - Enable to receive notifications"
              }
            />
            <ListItemSecondaryAction>
              <Switch
                checked={notificationPermission === 'granted'}
                onChange={handleNotificationToggle}
                disabled={notificationPermission === 'denied'}
              />
            </ListItemSecondaryAction>
          </ListItem>
          {notificationPermission === 'granted' && (
            <ListItem>
              <Button size="small" onClick={handleResetPush} disabled={loading}>
                {loading ? 'Resetting…' : 'Reset push subscription'}
              </Button>
            </ListItem>
          )}
          {notificationPermission === 'denied' && (
            <ListItem>
              <ListItemText
                secondary="In your browser, open Site settings for this app and allow Notifications. Then return here and toggle again."
              />
            </ListItem>
          )}
        </List>
      </Paper>
      
      {/* Privacy Section */}
      <Paper elevation={0} variant="glass" sx={{ mb: 2 }}>
        <List>
          <ListItem>
            <ListItemIcon>
              <Lock />
            </ListItemIcon>
            <ListItemText 
              primary="Privacy"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
          <Divider variant="inset" component="li" />
          
          <ListItem>
            <ListItemText 
              primary="Porchlight Visibility"
              secondary="Only friends you select can see when your porchlight is on"
            />
          </ListItem>
          
          <ListItem>
            <ListItemText 
              primary="Friend Requests"
              secondary="Only people with your email can send you friend requests"
            />
          </ListItem>
        </List>
      </Paper>
      
      {/* About Section */}
      <Paper elevation={0} variant="glass" sx={{ mb: 2 }}>
        <List>
          <ListItem>
            <ListItemIcon>
              <Info />
            </ListItemIcon>
            <ListItemText 
              primary="About"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
          <Divider variant="inset" component="li" />
          
          <ListItem>
            <ListItemText 
              primary="Porchlight"
              secondary="Version 1.0.0"
            />
          </ListItem>
          
          <ListItem button onClick={() => navigate(ROUTES.PRIVACY)}>
            <ListItemText primary="Privacy Policy" />
            <ChevronRight />
          </ListItem>
          
          <ListItem button onClick={() => navigate(ROUTES.TERMS)}>
            <ListItemText primary="Terms of Service" />
            <ChevronRight />
          </ListItem>
        </List>
      </Paper>
      
      {/* Actions */}
      <Paper elevation={0} variant="glass" sx={{ mb: 2 }}>
        <List>
          <ListItem button onClick={handleLogout} disabled={loading}>
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
          
          <ListItem button onClick={() => handleOpenDialog('deleteAccount')}>
            <ListItemIcon>
              <Delete color="error" />
            </ListItemIcon>
            <ListItemText 
              primary="Delete Account"
              primaryTypographyProps={{ color: 'error' }}
            />
          </ListItem>
        </List>
      </Paper>
      
      {/* Edit Profile Dialog */}
      <Dialog open={openDialog === 'editProfile'} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={{ elevation: 0, variant: 'glass' }}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            margin="normal"
            disabled={loading}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            margin="normal"
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>Cancel</Button>
          <Button onClick={handleUpdateProfile} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Change Password Dialog */}
      <Dialog open={openDialog === 'changePassword'} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={{ elevation: 0, variant: 'glass' }}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            value={formData.currentPassword}
            onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
            margin="normal"
            disabled={loading}
          />
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            margin="normal"
            helperText="At least 6 characters"
            disabled={loading}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm New Password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            margin="normal"
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>Cancel</Button>
          <Button onClick={handleChangePassword} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Account Dialog */}
      <Dialog open={openDialog === 'deleteAccount'} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={{ elevation: 0, variant: 'glass' }}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All your data will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete your account?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Alert */}
      <NotificationAlert
        notification={notification}
        severity={notification.severity}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Container>
  );
};

export default Settings;